"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useAuthWithLocationSync } from "@/hooks/use-auth-with-location-sync";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { trackAction } from "@/lib/track-action";

type RegisterFormProps = {
  redirectTo?: string;
  onSuccess?: () => void;
  onSwitchToLogin?: () => void;
  extraContent?: React.ReactNode;
  initialEmail?: string;
  initialCellphone?: string;
  initialPassword?: string;
};

export default function RegisterForm({ redirectTo = "/", onSuccess, onSwitchToLogin, extraContent, initialEmail = "", initialCellphone = "", initialPassword = "" }: RegisterFormProps) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState(initialEmail);
  const [cellphone, setCellphone] = useState(initialCellphone);
  const [password, setPassword] = useState(initialPassword);
  const [confirmPassword, setConfirmPassword] = useState(initialPassword);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { register, isLoading } = useAuthWithLocationSync();
  const router = useRouter();
  const isNonEmptyString = (str: string) => str.trim() !== "";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError("Las contraseñas no coinciden");
      return;
    }
    if (!isNonEmptyString(email) || !email.includes("@")) {
      setError("El email no es válido");
      return;
    }
    if (!isNonEmptyString(cellphone)) {
      setError("¿Cuál es tu número de celular?");
      return;
    }
    if (!isNonEmptyString(firstName)) {
      setError("¿Cuál es tu nombre?");
      return;
    }
    if (!isNonEmptyString(lastName)) {
      setError("¿Cuál es tu apellido?");
      return;
    }

    const result = await register({
      first_name: firstName,
      last_name: lastName,
      email,
      password,
      cellphone: cellphone || undefined,
    });

    if (result.ok) {
      trackAction("SUCCESSFUL-REGISTRATION");
      if (onSuccess) {
        onSuccess();
      } else {
        router.push(redirectTo);
      }
    } else {
      setError(result.error || "Error al crear cuenta");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName">Nombre</Label>
          <Input
            id="firstName"
            type="text"
            placeholder="Nombre"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            autoComplete="given-name"
            disabled={isLoading}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="lastName">Apellido</Label>
          <Input
            id="lastName"
            type="text"
            placeholder="Apellido"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            autoComplete="family-name"
            disabled={isLoading}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
          disabled={isLoading}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="cellphone">Celular</Label>
        <Input
          id="cellphone"
          type="tel"
          placeholder="Celular"
          value={cellphone}
          onChange={(e) => setCellphone(e.target.value)}
          autoComplete="tel"
          disabled={isLoading}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="password">Contraseña</Label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="new-password"
            disabled={isLoading}
            className="pr-10"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            tabIndex={-1}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="confirmPassword">Confirmar contraseña</Label>
        <div className="relative">
          <Input
            id="confirmPassword"
            type={showPassword ? "text" : "password"}
            placeholder="••••••••"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            autoComplete="new-password"
            disabled={isLoading}
            className="pr-10"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            tabIndex={-1}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <p className="text-center text-sm text-muted-foreground mt-4">
        ¿Ya tienes cuenta?{" "}
        {onSwitchToLogin ? (
          <button
            type="button"
            onClick={() => { trackAction("GO-TO-LOGIN"); onSwitchToLogin(); }}
            className="text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
          >
            Inicia sesión
          </button>
        ) : (
          <Link
            href={redirectTo !== "/" ? `/login?redirect=${encodeURIComponent(redirectTo)}` : "/login"}
            className="text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
          >
            Inicia sesión
          </Link>
        )}
      </p>

      {extraContent}

      <Button
        type="submit"
        className="w-full bg-teal-400 hover:bg-teal-500 cursor-pointer"
        size="lg"
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Creando cuenta...
          </>
        ) : (
          "Crear cuenta"
        )}
      </Button>
    </form>
  );
}
